import React from "react";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import type { SeverityNode as SeverityNodeType } from "@/lib/pharmacyFirst/types";

interface SeverityNodeProps {
  node: SeverityNodeType;
  isDarkMode?: boolean;
  onAnswer: (value: "mild" | "moderate" | "severe") => void;
  value?: "mild" | "moderate" | "severe" | null;
  stepNumber?: number;
}

const severityColors = {
  mild: { bg: "bg-green-50", border: "border-green-200", text: "text-green-900" },
  moderate: {
    bg: "bg-yellow-50",
    border: "border-yellow-200",
    text: "text-yellow-900",
  },
  severe: { bg: "bg-red-50", border: "border-red-200", text: "text-red-900" },
};

export function SeverityNode({
  node,
  isDarkMode = false,
  onAnswer,
  value = null,
  stepNumber,
}: SeverityNodeProps) {
  return (
    <div
      className={`border rounded-2xl p-4 ${
        isDarkMode ? "border-gray-700 bg-gray-800" : "border-gray-200 bg-white"
      }`}
    >
      {stepNumber !== undefined && (
        <div className="flex items-center gap-3 mb-4">
          <div
            className={`flex items-center justify-center w-6 h-6 rounded-full text-sm font-semibold ${
              isDarkMode ? "bg-white text-black" : "bg-black text-white"
            }`}
          >
            {stepNumber}
          </div>
          <h3
            className={`text-sm font-semibold ${
              isDarkMode ? "text-white" : "text-gray-900"
            }`}
          >
            {node.text}
          </h3>
        </div>
      )}

      {stepNumber === undefined && (
        <div className="mb-4">
          <h3
            className={`text-sm font-semibold ${
              isDarkMode ? "text-white" : "text-gray-900"
            }`}
          >
            {node.text}
          </h3>
        </div>
      )}

      <RadioGroup value={value || ""} onValueChange={(v: any) => onAnswer(v)}>
        <div className="space-y-2">
          {(["mild", "moderate", "severe"] as const).map((level) => {
            const colors = severityColors[level];
            return (
              <div
                key={level}
                className={`border rounded-lg p-3 cursor-pointer transition ${
                  value === level
                    ? `${colors.bg} ${colors.border} border-2`
                    : isDarkMode
                      ? "border-gray-600 hover:bg-gray-700"
                      : "border-gray-200 hover:bg-gray-50"
                }`}
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value={level} id={`${node.id}-${level}`} />
                  <label
                    htmlFor={`${node.id}-${level}`}
                    className={`text-sm font-medium cursor-pointer capitalize ${
                      value === level
                        ? colors.text
                        : isDarkMode
                          ? "text-gray-200"
                          : "text-gray-900"
                    }`}
                  >
                    {level}
                  </label>
                </div>
              </div>
            );
          })}
        </div>
      </RadioGroup>
    </div>
  );
}
